import React, { useContext, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon';
import { TimeResponse } from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import useForm from '@hooks/useForm/useForm';
import * as yup from 'yup';
import { Field } from '@components/framework/formfield/Field.tsx';
import { findValidationMessage } from '@utils/validation.tsx';
import { Calendar } from 'primereact/calendar';
import AddonsSvg from '@app/icons/AddonsSvg.tsx';
import {
  apiUtcFromDayjs,
  dateFromApiStringInTz,
  dateFromDayjs,
  dayjsFromApiUtcInTz,
  dayjsFromDayAndTimeStrings,
  dayjsFromLocalDateString,
  shortDateFormat,
  stringHHmmFromDayjs,
  stringHHmmFromStringInTz,
  stringYMDFromDate,
  stringYMDFromDayjs,
  dateToTimeString,
  timeStringToDate,
  apiPragueFromDayjsUtc,
} from '@utils/dateWithTz.tsx';
import { TwoColGrid } from '@components/ringil3/styles.tsx';
import { useTranslation } from 'react-i18next';
import SvgRoute from '@icons/route.svg?react';
import SvgMapPin from '@icons/map-pin.svg?react';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';


interface ComponentProps {
  applicationId: string;
  expeditionId: string;
  locationId: string;
  locationName: string;
  actionSlot: TimeResponse;
  onContinue: () => void;
}

interface FormData {
  manipulationDate?: string;
  manipulationTime?: string;
}

export const useEditActionSlotSidebar = () => {
  const [visible, setVisible] = useState(false);

  const EditActionSlotSidebar: React.FC<ComponentProps> = ({
                                                             onContinue,
                                                             applicationId,
                                                             expeditionId,
                                                             locationId,
                                                             locationName,
                                                             actionSlot,
                                                           }) => {
    const { tr } = useTranslationLgs();
    const { i18n } = useTranslation();
    const { finishLocationByCustomer } = useContext(ApiContext);
    const [loading, setLoading] = useState<boolean>(false);

    const save = (data: FormData) => {

      const timeZoneId = actionSlot?.timezoneId ?? actionSlot?.timeZone;

      const completedAtDayjs = dayjsFromDayAndTimeStrings(
        data.manipulationDate,
        data.manipulationTime,
        timeZoneId,
      );
      setLoading(true);
      finishLocationByCustomer(
        expeditionId,
        locationId,
        {
          completedAt: apiPragueFromDayjsUtc(completedAtDayjs, timeZoneId),
          completedAtUtc: apiUtcFromDayjs(completedAtDayjs)
        },
        () => {
          setLoading(false);
          setVisible(false);
          onContinue();
        }
      );
    };

    const getInitialValues = (): FormData => {
      if (actionSlot?.actionAtUtc && actionSlot?.timezoneId) {
        const inTz = dayjsFromApiUtcInTz(actionSlot.actionAtUtc, actionSlot.timezoneId);
        return {
          manipulationDate: stringYMDFromDayjs(inTz),
          manipulationTime: stringHHmmFromDayjs(inTz),
        };
      }
      return {
        manipulationDate: actionSlot?.localDate ?? '',
        manipulationTime: actionSlot?.hourString ?? '',
      };
    };

    const schema = yup.object().shape({
      manipulationDate: yup.string().required(),
      manipulationTime: yup.string().required(),
    });
    const form = useForm<FormData>(schema, getInitialValues(), d => save(d), false, false);

    if (!visible) return null;

    return (
      <>
        <RingilSidebar
          visible={visible}
          onBack={() => setVisible(false)}
          onContinue={() => form.validateAndSend()}
          heading={tr('EditActionSlotSidebar.changeActiveSlot', 'Chnage active slot')}
          loading={loading}
        >
          {/* Opts into the legacy PrimeReact overrides in styles/overrides/legacy-fields.scss. */}
          <div className={'r-legacy-fields contents'}>
          <VerticalFieldAndValueWithIcon
            label={tr('EditActionSlotSidebar.forExpedition', 'For expedition')}
            valueText={applicationId}
            icon={<SvgRoute className="text-content-tertiary" />}
          />
          <div className="mt-4">
            <VerticalFieldAndValueWithIcon
              label={tr('EditActionSlotSidebar.forLocation', 'For location')}
              valueText={locationName}
              icon={<SvgMapPin className="text-content-tertiary" />}
            >
              <COTimezone timezoneId={actionSlot?.timezoneId} />
            </VerticalFieldAndValueWithIcon>
          </div>


          <TwoColGrid className="mt-8">
            <Field
              label={tr('EditActionSlotSidebar.currentManipulationDate', 'Current manipulation date')}
              disabled
            >
              <Calendar
                locale={i18n.language}
                selectionMode={'single'}
                value={
                  (actionSlot?.actionAtUtc && actionSlot?.timezoneId)
                    ? dateFromApiStringInTz(actionSlot.actionAtUtc, actionSlot.timezoneId)
                    : actionSlot?.localDate ? new Date(actionSlot.localDate) : null
                }
                dateFormat={shortDateFormat}
                readOnlyInput
                disabled
              />
            </Field>
            <Field
              label={tr('EditActionSlotSidebar.currentManipulationTime', 'Current manipulation time')}
              disabled
            >
              <Calendar
                value={timeStringToDate(
                  (actionSlot?.actionAtUtc && actionSlot?.timezoneId)
                    ? stringHHmmFromStringInTz(actionSlot.actionAtUtc, actionSlot.timezoneId)
                    : actionSlot?.hourString
                )}
                timeOnly
                disabled
              />
            </Field>

          </TwoColGrid>
          <TwoColGrid className="mt-4">
            <Field
              label={tr('EditActionSlotSidebar.manipulationDate', 'New manipulation date')}
              required={!form.values.manipulationDate}
              errorMessage={findValidationMessage(form.validationErrors, form.names.manipulationDate, tr)}
            >
              <Calendar
                locale={i18n.language}
                selectionMode={'single'}
                value={form.values.manipulationDate ? dateFromDayjs(dayjsFromLocalDateString(form.values.manipulationDate)) : null}
                onChange={e =>
                  form.setFieldValue(form.names.manipulationDate, e.value instanceof Date ? stringYMDFromDate(e.value) : null)
                }
                showIcon
                icon={<AddonsSvg width={20} height={20} />}
                dateFormat={shortDateFormat}
                readOnlyInput
                showButtonBar={true}
                showWeek={true}
              />
            </Field>
            <Field
              label={tr('EditActionSlotSidebar.manipulationTime', 'New manipulation time')}
              required={!form.values.manipulationTime}
              errorMessage={findValidationMessage(form.validationErrors, form.names.manipulationTime, tr)}
            >
              <Calendar
                value={timeStringToDate(form.values.manipulationTime)}
                onChange={e =>
                  form.setFieldValue(form.names.manipulationTime, e.value && e.value instanceof Date ? dateToTimeString(e.value) : null)
                }
                timeOnly
              />
            </Field>
          </TwoColGrid>

          </div>
        </RingilSidebar>
      </>
    );
  };

  return { EditActionSlotSidebar, setVisible };
};


